import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Typography } from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { StyledProps } from 'styled-components';

import Box, { BoxContent } from '@/components/common/Box/Box';
import ComponentLoader from '@/components/common/Loaders/ComponentLoader';
import AsyncProfilePicture from '@/components/common/ProfilePicture/AsyncProfilePicture';
import { ExtendedUserData } from '@/domain/common';
import { COLOR, COLOR_FUNCTION, customScrollbar, PX_TO_REM } from '@/globalStyles';
import useModal from '@/hooks/useModal/useModal';
import useLoggedInUserData from '@/hooks/useUserData/useLoggedInUserData';
import useApproversLogic from '@/pages/NewRequest/useApproversLogic';
import EditApproversModal from '@/pages/Profile/common/ApproversBox/EditApproversModal/EditApproversModal';
import { isLastItemOfArray } from '@/utils/helpers/helpers';

export interface IApproversBoxProps {
    className?: string;
    boxTitle?: string;
    showEditButton?: boolean;
    userId?: string;
    canEdit?: boolean;
}

type ApproverProps = {
    hideBorder?: boolean;
};

const ApproversBox = ({ showEditButton, boxTitle, userId, className, canEdit }: IApproversBoxProps) => {
    const { t } = useTranslation();

    const { isModalOpen, onCloseModal, openModal } = useModal();
    const {
        approvers: userApprovers,
        allApprovers,
        getUserApproversIds,
        isLoading,
        isNoUserApprovers,
    } = useApproversLogic(userId);
    const { isAdmin, isLead } = useLoggedInUserData();

    const title = boxTitle ?? t('profile_common_approvers');
    const editIcon =
        showEditButton && canEdit ? <EditOutlinedIcon sx={{ cursor: 'pointer' }} onClick={openModal} /> : null;

    const showFallbackApprovers = !isNoUserApprovers;
    const isAdminOrLead = isAdmin || isLead;

    return (
        <Box title={title} headerRightPanel={editIcon} className={className}>
            <ComponentLoader isLoading={isLoading} unmountChildren>
                {showFallbackApprovers ? (
                    userApprovers?.map(
                        (approver, index) =>
                            approver && (
                                <Approver
                                    key={`${approver.credentialId}`}
                                    hideBorder={isLastItemOfArray(userApprovers.length, index)}
                                >
                                    <AsyncProfilePicture
                                        size={PX_TO_REM[40]}
                                        profilePictureUrl={approver.profilePictureUrl}
                                    />
                                    <Description>
                                        <Typography variant="table_text">{`${approver.firstName} ${approver.lastName}`}</Typography>
                                        <Typography color={COLOR.darkGray} variant="little_text">
                                            {isAdminOrLead ? approver.email : undefined}
                                        </Typography>
                                    </Description>
                                </Approver>
                            )
                    )
                ) : (
                    <NoDataWrapper>
                        <Typography color={COLOR.darkGray} variant="created_by">
                            {t('coworkerApproversView_noApproversDefinied_title')}
                        </Typography>
                    </NoDataWrapper>
                )}
            </ComponentLoader>

            {isModalOpen && (
                <EditApproversModal
                    allApprovers={allApprovers}
                    userApprovers={userApprovers as ExtendedUserData[] | undefined}
                    isModalOpen={isModalOpen}
                    onCloseModal={onCloseModal}
                    getUserApproversList={getUserApproversIds}
                    userId={userId}
                    isReadOnly={!canEdit}
                />
            )}
        </Box>
    );
};

const Approver = styled.div<StyledProps<ApproverProps>>`
    display: flex;
    align-items: center;
    padding: 0.625rem 0;
    border-bottom: 1px solid ${COLOR_FUNCTION.border};
    border-color: ${props => (props.hideBorder ? 'transparent' : '')};
`;

const Description = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-left: 1rem;

    & > span:last-of-type {
        margin-top: 0.125rem;
    }
`;

const NoDataWrapper = styled.div`
    padding: 0.625rem 0;
`;

const Memoized = memo(ApproversBox);
const Styled = styled(Memoized)`
    ${BoxContent} {
        display: flex;
        flex-direction: column;
        overflow-x: auto;
        max-height: 12rem;
        padding: 0 0.625rem;
        ${customScrollbar}
    }
    ${ComponentLoader} {
        min-height: 2rem;
    }
`;

export default Styled;
